import { useMutation } from '@tanstack/react-query';
import { db } from '../../lib/firebase';
import { doc, writeBatch, collection, getDocs, query, limit } from 'firebase/firestore';
import { tenantManagementApi } from '../../api/tenantManagement';
import { generateCitySitemap } from '../../api/cities';

interface UpdateCitySubscriptionParams {
  organizationId: string;
  selectedCities: string[];
  currentCities: string[];
}

/**
 * Custom hook to update city subscriptions
 * Handles both adding new cities and removing unselected cities from Firestore
 */
export const useUpdateCitySubscription = () => {
  return useMutation({
    mutationFn: async ({
      organizationId,
      selectedCities,
      currentCities,
    }: UpdateCitySubscriptionParams) => {
      // Find cities to remove (cities in current but not in selected)
      const citiesToRemove = currentCities.filter((city) => !selectedCities.includes(city));

      // Only proceed if there are changes
      const citiesChanged =
        selectedCities.length !== currentCities.length ||
        selectedCities.some((city) => !currentCities.includes(city));

      if (!citiesChanged) {
        return { success: true, message: 'No changes detected' };
      }

      // Create a batch for Firestore operations
      const batch = writeBatch(db);

      // Remove unselected cities from Firestore
      if (citiesToRemove.length > 0) {
        for (const cityId of citiesToRemove) {
          // Delete from subscribedCities subcollection
          const subscribedCityRef = doc(db, 'tenants', organizationId, 'subscribedCities', cityId);
          batch.delete(subscribedCityRef);

          // Remove from landingPages config
          const landingConfigRef = doc(db, 'tenants', organizationId, 'config', 'landingPages');

          // Get current config to update it
          const landingConfigDoc = await getDocs(
            query(collection(db, 'tenants', organizationId, 'config'), limit(1))
          );

          if (!landingConfigDoc.empty) {
            const configData = landingConfigDoc.docs[0].data();
            const cities = configData.cities || {};

            // Remove the city from the config
            delete cities[cityId];

            batch.set(
              landingConfigRef,
              {
                cities: cities,
              },
              { merge: true }
            );
          }
        }

        // Commit the batch delete operations
        await batch.commit();
      }

      // Subscribe to new/remaining cities using the API
      await tenantManagementApi.subscribeToCity({
        tenant_id: organizationId,
        city_ids: selectedCities,
      });

      // Generate sitemap for the updated cities
      await generateCitySitemap(organizationId);

      return {
        success: true,
        message: 'City subscriptions updated successfully',
        added: selectedCities.filter((city) => !currentCities.includes(city)),
        removed: citiesToRemove,
      };
    },
  });
};
